import { useQueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { Platform } from 'react-native';

import { formEditingState } from './FormEditingContext';
import { useAppStateManager } from '../hooks/useAppStateManager';
import { setSessionExpirationHandler, setAuthError } from '../lib/queryClient';
import { runAppVersionCheck } from '../services/appVersionCheck';
import {
  getAuthService,
  getCurrentProvider,
} from '../services/auth/authServiceFactory';
import { roleService } from '../services/auth/roleService';
import {
  bindDomainMembershipIfNeeded,
  clearAfterLoginFlag,
  clearBindPendingOnLogout,
} from '../services/bindDomainMembership';
import { setDisplayTimeZone } from '../services/displayTimeZone';
import { networkService } from '../services/network';
import { offlineCache } from '../services/offlineCache';
import { PermissionsService } from '../services/permissionsService';
import { syncBadge } from '../services/push/pushRegistration';
import { clearAllRequestDraftSnapshots } from '../services/requestDraftSnapshot';
import { reportDeviceTimeZone } from '../services/userTimeZone';
import { AuthContextType, AuthState, User } from '../types/auth';
import { clearRequestListViewState } from '../utils/requestListViewState';
import { withTimeout, TimeoutError, TIMEOUT_CONSTANTS } from '../utils/timeout';

// Type declarations for web-only APIs
declare const window: any;

// Auth provider confirmed at startup via factory

const initialAuthState: AuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: true,
  error: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(initialAuthState);
  const [permissionsHydrationVersion, setPermissionsHydrationVersion] =
    useState(0);
  const queryClient = useQueryClient();
  const previousUserIdRef = React.useRef<string | null>(null);
  const userRef = React.useRef<User | null>(null);
  const [authService] = useState(() => {
    try {
      const service = getAuthService();

      if (!service) {
        throw new Error('Auth service factory returned null/undefined');
      }

      if (typeof service.login !== 'function') {
        throw new Error('Auth service missing login method');
      }

      return service;
    } catch (error) {
      console.error('AuthProvider: Failed to initialize auth service:', error);
      throw new Error('Failed to initialize authentication service');
    }
  }); // Initialize once using factory pattern

  // No OAuth callback handling needed - MSAL Browser handles automatically, Native uses server sessions

  const checkAuthStatus = useCallback(
    async (options?: { allowNativeCachedRestore?: boolean }) => {
      try {
        setAuthState(prev => ({ ...prev, isLoading: true }));

        if ((await runAppVersionCheck()) === 'blocked') {
          setAuthState(prev => ({ ...prev, isLoading: false }));
          return;
        }

        if (!authService) {
          console.error('checkAuthStatus: authService is undefined');
          throw new Error('Auth service not initialized');
        }

        const status = await authService.getAuthStatus();

        if (status.kind === 'error') {
          if (
            options?.allowNativeCachedRestore === true &&
            Platform.OS !== 'web' &&
            !userRef.current
          ) {
            const cachedUser = await authService.getCurrentUser();
            if (cachedUser) {
              previousUserIdRef.current = cachedUser.id;
              userRef.current = cachedUser;
              setAuthState({
                isAuthenticated: true,
                user: cachedUser,
                isLoading: false,
                error: null,
              });
              void reportDeviceTimeZone();
              return;
            }
          }
          userRef.current = null;
          setDisplayTimeZone(null);
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: 'Unable to verify your session.',
          });
          return;
        }

        if (status.kind === 'anonymous') {
          await clearAfterLoginFlag();
          setDisplayTimeZone(null);
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: null,
          });
          userRef.current = null;
          return;
        }

        const user = status.user;

        if (
          previousUserIdRef.current !== null &&
          previousUserIdRef.current !== user.id
        ) {
          queryClient.clear();
        }

        previousUserIdRef.current = user.id;
        userRef.current = user;

        setAuthState({
          isAuthenticated: true,
          user,
          isLoading: false,
          error: null,
        });

        void Promise.resolve(bindDomainMembershipIfNeeded({ user })).catch(
          () => undefined,
        );
        void reportDeviceTimeZone();
      } catch (error) {
        console.error('❌ AuthContext: Failed to check auth status:', error);
        userRef.current = null;
        setDisplayTimeZone(null);
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
          error: 'Unable to verify your session.',
        });
      }
    },
    [authService, queryClient],
  );

  // Check for existing session on mount
  useEffect(() => {
    void checkAuthStatus({ allowNativeCachedRestore: true });
  }, [checkAuthStatus]);

  useEffect(() => {
    let previousConnected = false;
    const unsubscribe = networkService.addListener(isConnected => {
      const wasConnected = previousConnected;
      previousConnected = isConnected;
      if (!wasConnected && isConnected) {
        const user = userRef.current;
        if (!user?.userId) {
          return;
        }
        void Promise.resolve(bindDomainMembershipIfNeeded({ user })).catch(
          () => undefined,
        );
      }
    });

    void Promise.resolve(networkService.initialize())
      .then(() => {
        const connected =
          networkService.isInitialized() && networkService.isConnected();
        previousConnected = connected;
        if (!connected) {
          return;
        }
        const user = userRef.current;
        if (!user?.userId) {
          return;
        }
        void Promise.resolve(bindDomainMembershipIfNeeded({ user })).catch(
          () => undefined,
        );
      })
      .catch(() => undefined);

    return unsubscribe;
  }, []);

  // Coarse role-permission hydration — fires on every isAuthenticated transition, in either
  // direction, so it covers login(), the mount-time checkAuthStatus() path, session expiration,
  // and the useAppStateManager onActive invalidation path uniformly (no need to sprinkle a
  // manual resetRolePermissions() call into each of those functions individually). Uses .then()
  // chains rather than async/await inside the effect body, per this codebase's auth-effect rule
  // (see src/contexts/ImpersonationContext.tsx for the established precedent). The `cancelled`
  // flag guards against a slow fetch resolving after a since-logged-out transition — React runs
  // this effect's cleanup (setting cancelled = true for that closure) before the next
  // invocation, so a late .then() checks cancelled and discards its result instead of
  // re-hydrating stale data into a logged-out state.
  //
  // permissionsHydrationVersion is bumped after every hydrate/reset — roleService is a plain
  // singleton with no subscription mechanism, so any consumer that memoizes a roleService
  // permission check (e.g. useFormPermissions' rolePermissions) needs a real React state change
  // to know to recompute once hydration lands; without this, a component already mounted at
  // login time would keep showing fallback-derived edit/create/view/close booleans forever.
  useEffect(() => {
    let cancelled = false;

    if (authState.isAuthenticated) {
      PermissionsService.fetchRolePermissions()
        .then(map => {
          if (cancelled) return;
          roleService.hydrateRolePermissions(map);
          setPermissionsHydrationVersion(v => v + 1);
          return offlineCache.cacheRolePermissions(map);
        })
        .catch(error => {
          console.warn(
            'Failed to hydrate role permissions from API, falling back:',
            error,
          );
          offlineCache.getCachedRolePermissions().then(cached => {
            if (cancelled) return;
            if (cached) {
              roleService.hydrateRolePermissions(cached);
              setPermissionsHydrationVersion(v => v + 1);
            }
            // else: stays on fallbackRolePermissions untouched — already the default state.
          });
        });
    } else {
      roleService.resetRolePermissions();
      setPermissionsHydrationVersion(v => v + 1);
    }

    return () => {
      cancelled = true;
    };
  }, [authState.isAuthenticated]);

  const forgetBindSession = useCallback(async () => {
    try {
      await clearBindPendingOnLogout(userRef.current);
    } catch {
      // Still clear auth even if pending delete throws
    }
    userRef.current = null;
  }, []);

  // Register session expiration handler with query client
  useEffect(() => {
    const handleSessionExpiration = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        if (currentUser?.email) {
          await clearAllRequestDraftSnapshots(currentUser.email);
        }
      } catch (error) {
        console.warn(
          'Failed to clear request drafts on session expiration:',
          error,
        );
      }

      await forgetBindSession();

      setDisplayTimeZone(null);
      // Clear auth state - session expired
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: 'Your session has expired. Please log in again.',
      });

      queryClient.clear();

      // Attempt to logout (clear server session)
      try {
        await authService.logout();
      } catch (error) {
        console.error('Error during session expiration logout:', error);
      }

      // Reset auth error flag after successful logout
      setAuthError(false);

      // Clear URL path and navigate to landing page on web
      if (typeof window !== 'undefined' && window.history) {
        window.history.pushState({}, '', '/');
      }
    };

    // Register the handler with the query client
    setSessionExpirationHandler(handleSessionExpiration);

    // Cleanup on unmount
    return () => {
      setSessionExpirationHandler(() => {});
    };
  }, [authService, forgetBindSession, queryClient]);

  // Unified session validation using useAppStateManager hook
  // Replaces duplicate listeners to fix race conditions and improve reliability
  useAppStateManager({
    onActive: useCallback(async () => {
      // Only validate if authenticated
      if (!authState.isAuthenticated) {
        return;
      }

      // #1642: refresh the app-icon badge every time the app comes forward,
      // so the number FALLS as the person reads things on another device. It
      // lives here rather than in `usePushNotifications` because this hook
      // must be used exactly once in the app and this is that one use. No
      // await: the badge must not delay session validation, and `syncBadge`
      // never throws.
      void syncBadge();

      // Validate session on app becoming active

      try {
        // Validate session with 10-second timeout to prevent indefinite hangs
        const user = await withTimeout(
          authService.getCurrentUser(),
          TIMEOUT_CONSTANTS.AUTH_CHECK,
          'Session validation',
        );

        if (!user) {
          // Set auth error flag to prevent stale placeholder data
          setAuthError(true);

          await forgetBindSession();

          setDisplayTimeZone(null);
          // Session invalid, trigger logout
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: 'Your session has expired. Please log in again.',
          });

          queryClient.clear();

          // Navigate to login on web
          if (typeof window !== 'undefined' && window.history) {
            window.history.pushState({}, '', '/');
          }
        } else {
          // Reset auth error flag
          setAuthError(false);

          // Check if user is actively editing a form
          // If so, skip query invalidation to prevent data loss
          if (!formEditingState.isEditing()) {
            // Smart cache management: invalidate instead of clearing
            // This allows React Query to show stale data while refetching
            queryClient.invalidateQueries();
          }
        }
      } catch (error) {
        if (error instanceof TimeoutError) {
          // Timeout - likely slow network, don't logout
          console.warn('⏱️ Session validation timed out - using cached state');
          // Cache remains intact, user stays logged in
        } else {
          console.error('❌ Session validation error:', error);
          // Network errors - don't logout, could be offline
        }
      }
    }, [
      authState.isAuthenticated,
      authService,
      forgetBindSession,
      queryClient,
    ]),

    debounceMs: 300, // Prevent rapid-fire validation calls
    debug: __DEV__, // Enable debug logging in development
  });

  const login = useCallback(
    async (userId?: string) => {
      try {
        setAuthState(prev => ({
          ...prev,
          isLoading: true,
          error: null,
        }));

        const user = await authService.login(userId);

        queryClient.clear();

        userRef.current = user;
        previousUserIdRef.current = user.id;

        setAuthState({
          isAuthenticated: true,
          user,
          isLoading: false,
          error: null,
        });

        if (Platform.OS !== 'web') {
          void Promise.resolve(
            bindDomainMembershipIfNeeded({ user, completedLogin: true }),
          ).catch(() => undefined);
        }
        void reportDeviceTimeZone();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Authentication failed';

        setDisplayTimeZone(null);
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
          error: errorMessage,
        });

        throw error; // Re-throw to allow handling in the component
      }
    },
    [authService, queryClient],
  );

  const logout = useCallback(async () => {
    const userEmail = authState.user?.email;
    try {
      if (userEmail) {
        await clearAllRequestDraftSnapshots(userEmail);
        clearRequestListViewState(userEmail);
      }
      await forgetBindSession();
      await authService.logout();

      queryClient.clear();

      setDisplayTimeZone(null);
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: null,
      });

      // Clear URL path and navigate to landing page on web
      if (typeof window !== 'undefined' && window.history) {
        window.history.pushState({}, '', '/');
      }
    } catch (error) {
      console.error('Logout error:', error);

      if (userEmail) {
        await clearAllRequestDraftSnapshots(userEmail).catch(() => undefined);
        clearRequestListViewState(userEmail);
      }

      await forgetBindSession();

      queryClient.clear();

      setDisplayTimeZone(null);
      // Still clear state even if logout fails
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: null,
      });

      // Clear URL path even if logout fails on web
      if (typeof window !== 'undefined' && window.history) {
        window.history.pushState({}, '', '/');
      }
    }
  }, [authService, forgetBindSession, queryClient, authState.user?.email]);

  const clearError = useCallback(() => {
    setAuthState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * Validates the current session by checking if the user is still authenticated
   * Returns true if session is valid, false otherwise
   * Sets auth error flag if session is invalid
   * Phase 4.1: Validates with auth service
   */
  const validateSession = useCallback(async (): Promise<boolean> => {
    try {
      const user = await authService.getCurrentUser();

      if (!user) {
        console.warn(
          'Session validation failed - user no longer authenticated',
        );
        setAuthError(true, 'manual');
        return false;
      }

      // Reset auth error flag on successful validation
      setAuthError(false);
      return true;
    } catch (error) {
      console.error('Session validation error:', error);

      // Don't set auth error flag for network errors - could be offline
      return false;
    }
  }, [authService]);

  /**
   * Force-refresh authentication state by clearing cached user data
   * and re-fetching from the server. Used after impersonation start/end
   * when the JWT cookie has been swapped.
   */
  const refreshAuth = useCallback(async (): Promise<void> => {
    if (authService.clearUserCache) {
      authService.clearUserCache();
    }
    await checkAuthStatus();
  }, [authService, checkAuthStatus]);

  const contextValue: AuthContextType = useMemo(
    () => ({
      ...authState,
      login,
      logout,
      clearError,
      validateSession,
      refreshAuth,
      permissionsHydrationVersion,
    }),
    [
      authState,
      login,
      logout,
      clearError,
      validateSession,
      refreshAuth,
      permissionsHydrationVersion,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
